/**
 *
 * Select
 *
 */

import React from 'react';
import {
  Label,
  Input,
  Wrapper,
  Option,
  Options,
  SelectIcon
} from './styledComponents';

import InputValidator from '../InputValidator';
import Icon from '../Icon';
import DraggableList from '../DraggableList';
import Tooltip from 'components/Tooltip';
import { colors } from 'globalStyles'

class Select extends React.PureComponent {
  // Local initial state on mount
  state = {
    isOpen: false,
    inputValue: '',
    fromOptions: true,
    shouldReload: false,
  };

  // Lifecycle hooks
  componentDidMount() {
    document.addEventListener('mousedown', this.onOutsideClick);
    document.addEventListener('keydown', this.onKeyDown);
    if (this.props.input.value) {
      this.handleOptionSelected(this.props.input.value);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onOutsideClick);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.input.value !== this.props.input.value && !this.props.isMultiSelect) {
      var newInputVal = this.props.input.value;
      const option = (this.props.options || []).find(x => x.value === newInputVal);
      this.updateInputValue(option ? option.label : newInputVal);
    }

    if (!prevState.isOpen && this.state.isOpen) {
      const list = document.getElementsByClassName('options-list');
      if (list && list.length > 0) {
        list[0].addEventListener('scroll', this.handleFetchOptionsScroll);
      }
    }

    if (prevProps.input.value && !this.props.input.value) {
      this.setState({ shouldReload: true })
    }
    if (this.state.shouldReload) {
      this.setState({ shouldReload: false })
    }
  }

  componentWillUpdate(_nextProps, _nextState) {
    if (!_nextState.isOpen && this.state.isOpen) {
      const list = document.getElementsByClassName('options-list');
      if (list && list.length > 0) {
        list[0].removeEventListener('scroll', this.handleFetchOptionsScroll);
      }
    }
  }

  /**
  *
  * Recursively check for parent node containing the ref node
  * return true if the target or parent nodes do not match
  * the refArea
  *
  * @param  {object|undefined} target A node or parent node of passed in target
  *
  * @return {boolean}
  */
  clickWasOutside = (target) => (
    target === this.refArea
      ? false
      : !!target
        ? this.clickWasOutside(target.parentNode)
        : true
  )

  onOutsideClick = (e) => {
    e.stopPropagation();

    if (this.state.isOpen && this.clickWasOutside(e.target)) {
      this.closeOptions();
    }
  }

  onKeyDown = (e) => {
    // close on escape, tab, and enter
    const CLOSE_ON = [27, 9, 13];
    if (this.state.isOpen && CLOSE_ON.includes(e.keyCode)) {
      e.preventDefault();
      this.closeOptions();
    }
  }

  handleFetchOptionsScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight === el.scrollHeight) {
      const { pagingOptions, fetchOptions } = this.props;
      if (pagingOptions && fetchOptions && pagingOptions.currentPage !== pagingOptions.pageCount) {
        fetchOptions({ page: pagingOptions.currentPage + 1, search: this.state.inputValue })
      }
    }
  }

  filterOptions = (opts = [], val = '') => {
    const selectedList = this.props.input.value || [];
    return !val
      ? opts
      : (this.props.isMultiSelect
        ? opts.filter(({ value }) => !selectedList.includes(value))
        : opts.filter(({ label }) => label.toLowerCase().substr(0, val.length) === val.toLowerCase()))
  }

  filteredOptions = () => (
    this.filterOptions(this.props.options, this.state.inputValue)
  );

  handleOptionSelected = (value) => {
    const { options, input, isMultiSelect } = this.props;
    const { label } = options ? options.find(opt => (opt.value === value)) || { label: this.props.input.value } : { label: value };
    var newValue = value;
    if (isMultiSelect) {
      var selectedList = input.value || [];
      selectedList.push(value);
      newValue = selectedList;
    }
    input.onChange(newValue);

    this.setState({
      isOpen: false,
      inputValue: !isMultiSelect ? label : '',
      fromOptions: true,
    });
  }

  handleInputChange = (v) => {
    const { fetchOptions, alwaysFetchOptions, pagingOptions } = this.props;
    if (fetchOptions && (!this.filteredOptions().length || alwaysFetchOptions)) {
      fetchOptions(pagingOptions ? { page: 1, search: v } : v);
    }

    if (this.state.inputValue !== v) {
      this.setState({
        isOpen: true,
        inputValue: v,
        fromOptions: false,
      });
    }
  }

  // Simple state update functions
  openOptions = () => {
    if (!this.state.isOpen) {
      this.setState({ isOpen: true });
    }
  }

  closeOptions = () => {
    if (this.state.isOpen) {
      this.setState({ isOpen: false });
    }
  }

  updateInputValue = (v) => {
    this.setState({ inputValue: v });
  }

  useInputAndClose = () => {
    const { input } = this.props;

    input.onChange(this.state.inputValue);

    this.setState({ 
      isOpen: false, 
      fromOptions: false });
  }

  getValueStr = (path) => {
    var isImagePath = path && (path.includes("png") || path.includes("jpg"));
    var result = isImagePath ? path.replace(/\?.*/g, "") : path;
    var pathSplits = result ? result.split('/') : [];

    return isImagePath && pathSplits ? pathSplits[pathSplits.length-1] : path;
  }

  render() {
    const {
      input: { onBlur, ...input },
      label,
      placeholder,
      meta: { touched, error, valid },
      inputClassName,
      labelClassName,
      validIconName,
      errorIconName,
      emptyIconName,
      newOptionText,
      optionsContainerClass,
      CustomOption,
      leftIcon,
      enableRemoveIcon,
      isFetching,
      isMultiSelect,
      inputDisabled,
      listChanged,
      listSelectChanged,
      tooltip,
      enableDropDownIcon,
      permissionRole,
      brandForm
    } = this.props;

    const {
      isOpen,
      inputValue,
      fromOptions,
      shouldReload,
    } = this.state;

    const hasError = touched && error,
      isValid = touched && valid;

    const noop = () => noop => noop;
    const isExact = (v1, v2) => v1 === v2 && v1.length === v2.length;

    const filteredOptions = this.filteredOptions();
     const liteUserHasFilter =
       input.name === "photo_filters" &&
       permissionRole === "lite" &&
       brandForm["photo_filters"] &&
       brandForm["photo_filters"].length
         ? true
         : false;
  
    return (
      <Wrapper
        onClick={this.openOptions}
        innerRef={ref => this.refArea = ref}>
        {isMultiSelect && (
          <DraggableList
            data={input.value}
            listChanged={(list) => listChanged && listChanged(list)}
            listSelectChanged={listSelectChanged} />
        )}
        {!shouldReload && (
          <Input
            {...input}
            value={inputValue && inputValue.length ? this.getValueStr(inputValue) : ""}
            onChange={(e) => this.handleInputChange(e.target.value)}
            onBlur={
              // Block unwanted change triggers with fancy tricks
              filteredOptions.find(({ label }) => isExact(label, inputValue))
                ? noop
                : onBlur
            }
            hasError={hasError}
            className={`${inputClassName} ${leftIcon ? 'has-icon' : ''} ${isMultiSelect && input.value ? 'multi-select' : ''}`}
            placeholder={liteUserHasFilter ? "lite tier users are limited to one photo filter": placeholder}
            disabled={inputDisabled}
          />)}
        {label && <Label className={`${labelClassName} ${isMultiSelect ? 'multi-select' : ''}`}>
          {label}
        </Label>}
        {leftIcon && <SelectIcon>{leftIcon}</SelectIcon>}
        {enableRemoveIcon && inputValue && (
          <SelectIcon
            className='right'
            onClick={() => {
              this.handleInputChange('');
              this.handleOptionSelected('');
            }}>
            <Icon name='x' size='10' />
          </SelectIcon>
        )}
        {enableDropDownIcon && (
          <SelectIcon className='right'>
            <Icon name='drop-down' size='12' fill={colors.DARK} />
          </SelectIcon>
        )}
        {isOpen && (newOptionText || isFetching || (filteredOptions && filteredOptions.length > 0)) &&
          <Options className={`options-list ${optionsContainerClass || ''}`}>
            {newOptionText
              ?
              <Option
                onClick={this.useInputAndClose}
                selected={!fromOptions}>
                {`${newOptionText} "${this.state.inputValue}"`}
              </Option>
              : null
            }
            {filteredOptions.map(({ value, label }) => {
              const optionProps = {
                key: value,
                onClick: () => this.handleOptionSelected(value),
                selected: fromOptions && input.value === value,
                label: label,
                value: value,
              };

              return CustomOption
                ? <CustomOption {...optionProps} />
                : <Option {...optionProps}>{label}</Option>
            }
            )}
            {isFetching
              ?
              <Option>Loading...</Option>
              : null
            }
          </Options>
        }
        <InputValidator
          hasError={hasError}
          isValid={isValid}
          validIconName={validIconName}
          errorIconName={errorIconName}
          emptyIconName={emptyIconName}
        />
        {tooltip && <Tooltip className='tooltip' width='200px' text={tooltip}>
          <Icon size='12' name='help' />
        </Tooltip>}
      </Wrapper>
    );
  }
}

export default Select;



// WEBPACK FOOTER //
// ./src/components/InputField/Select.js